"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback } from "react";
import { Icon } from "@/components/formatters/MXPIcon";
import { MarketplaceCard, type MarketplaceCardProps } from "@/components/client/MarketplaceCard/MarketplaceCard";
import { FilterableCard, type FilterableCardProps } from "./FilterableCard";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { layoutHeaderClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import { FilterSidebar } from "./FilterSidebar";
import { Pagination } from "./Pagination";
import { MobileFilterDrawer } from "./MobileFilterDrawer";
import type { FilterGroup, SortOption } from "./types";
import type { FilterableCardsLabelsFields } from "@/queries/fragments/filterableCardsLabelsFragment";
export type { FilterGroup, SortOption, FilterOption } from "./types";
export type { FilterableCardProps } from "./FilterableCard";
import "./LayoutFilterableCards.css";

/**
 * Display type values authored in Contentful on `layoutFilterableCards`.
 * Determines which card component (and matching prop shape) the grid renders.
 */
export type LayoutFilterableCardsDisplayType = "Filterable Card" | "Page: General";

/**
 * One grid item. The shape depends on the layout's {@link LayoutFilterableCardsProps.displayType}:
 * - `"Filterable Card"` → {@link FilterableCardProps} (renders {@link FilterableCard})
 * - `"Page: General"` (default) → {@link MarketplaceCardProps} (renders {@link MarketplaceCard})
 */
export type LayoutFilterableCardItem = FilterableCardProps | MarketplaceCardProps;
export interface LayoutFilterableCardsProps {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  subheader?: string;
  description?: string;
  buttons?: TerraLink[];
  presetTheme?: "t2-theme-one" | "t2-theme-two" | "t2-theme-three" | "t2-theme-four" | "t2-theme-five" | "t2-theme-six" | "t2-theme-seven";
  isHeaderBig?: boolean;
  isWrapped?: boolean;

  /**
   * Default {@link MarketplaceCard} image mode when a card omits `imageDisplayMode`.
   * @default "logo"
   */
  imageDisplayMode?: "logo" | "teaser";
  cards?: LayoutFilterableCardItem[];
  totalResults: number;
  filterGroups?: FilterGroup[];
  activeFilters?: Record<string, string[]>;
  onFilterChange?: (groupId: string, values: string[]) => void;
  onClearAllFilters?: () => void;
  sortOptions?: SortOption[];
  currentSort?: string;
  onSortChange?: (value: string) => void;
  currentPage?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;

  /**
   * Opt-in: drops the 1440px outer max-width, reduces large-screen horizontal
   * padding, and unlocks a 4-column grid at the `2xl` breakpoint (≥1536px).
   * Intended for the in-product iframe embed where the host page (e.g.
   * Trimble Connect) wants the catalog to fill more horizontal space on large
   * monitors. Defaults to false so public marketplace pages and Storybook
   * stories keep their existing capped layout.
   */
  fullWidth?: boolean;
  /**
   * Optional client-side search input rendered above the grid. When
   * `onSearchChange` is omitted the input is not rendered.
   */
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  searchPlaceholder?: string;
  isLCP?: boolean;
  /**
   * Selects which card component renders inside the grid.
   * - `"Filterable Card"` → {@link FilterableCard} (cards typed as {@link FilterableCardProps})
   * - `"Page: General"` (default) → {@link MarketplaceCard} (cards typed as {@link MarketplaceCardProps})
   */
  displayType?: LayoutFilterableCardsDisplayType;
  /** Filterable cards labels from `siteConfig.filterableCardsLabels`. */
  labels?: FilterableCardsLabelsFields | null;
}
const CARD_WRAP = "flex h-full w-full mb-6 max-w-[450px] justify-center items-stretch";
const SORT_SELECT = "border border-gray-m3 rounded-lg px-3 py-2 text-sm bg-white text-gray-m9 cursor-pointer min-w-[200px] appearance-none";
const MOBILE_FILTER_SORT_BAR = "sticky z-40 flex flex-col gap-2 py-3 -mt-2 mb-4 bg-white t2-dark:bg-gray-m10 border-b border-gray-m2 shadow-sm";
function getSiteNavigationStickyTop(): number {
  const siteNav = document.querySelector(".site-navigation") as HTMLElement | null;
  if (!siteNav) return 0;
  return siteNav.classList.contains("site-navigation--is-hidden") ? 0 : siteNav.offsetHeight;
}
export const LayoutFilterableCards = t0 => {
  const $ = _c(111);
  const {
    eyebrow,
    header,
    headerElement: t1,
    subheader,
    description,
    buttons,
    presetTheme,
    isWrapped,
    imageDisplayMode: t2,
    cards: t3,
    totalResults,
    filterGroups: t4,
    activeFilters: t5,
    onFilterChange,
    onClearAllFilters,
    sortOptions: t6,
    currentSort,
    onSortChange,
    currentPage: t7,
    pageSize: t8,
    pageSizeOptions,
    onPageChange,
    onPageSizeChange,
    searchTerm,
    onSearchChange,
    searchPlaceholder,
    isLCP: t9,
    displayType,
    labels
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const imageDisplayMode = t2 === undefined ? "logo" : t2;
  let t10;
  if ($[0] !== t3) {
    t10 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const cards = t10;
  let t11;
  if ($[2] !== t4) {
    t11 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const filterGroups = t11;
  let t12;
  if ($[4] !== t5) {
    t12 = t5 === undefined ? {} : t5;
    $[4] = t5;
    $[5] = t12;
  } else {
    t12 = $[5];
  }
  const activeFilters = t12;
  let t13;
  if ($[6] !== t6) {
    t13 = t6 === undefined ? [] : t6;
    $[6] = t6;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const sortOptions = t13;
  const currentPage = t7 === undefined ? 1 : t7;
  const pageSize = t8 === undefined ? 30 : t8;
  const isLCP = t9 === undefined ? false : t9;
  const resolvedSearchPlaceholder = searchPlaceholder ?? (labels?.labelForSearchApps || "Search apps...");
  const {
    width
  } = useScreenSize();
  const isMobile = width > 0 && width < 1024;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("filter");
  const [mobileStickyTop, setMobileStickyTop] = useState(0);
  let t14;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setMobileStickyTop(getSiteNavigationStickyTop());
    };
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  const updateMobileStickyTop = t14;
  let t15;
  let t16;
  if ($[9] !== isMobile) {
    t15 = () => {
      if (!isMobile) {
        return;
      }
      updateMobileStickyTop();
      const siteNav = document.querySelector(".site-navigation") as HTMLElement | null;
      if (!siteNav) {
        return;
      }
      const classObserver = new MutationObserver(updateMobileStickyTop);
      classObserver.observe(siteNav, {
        attributes: true,
        attributeFilter: ["class"]
      });
      const resizeObserver = new ResizeObserver(updateMobileStickyTop);
      resizeObserver.observe(siteNav);
      window.addEventListener("resize", updateMobileStickyTop, {
        passive: true
      });
      return () => {
        classObserver.disconnect();
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateMobileStickyTop);
      };
    };
    t16 = [isMobile, updateMobileStickyTop];
    $[9] = isMobile;
    $[10] = t15;
    $[11] = t16;
  } else {
    t15 = $[10];
    t16 = $[11];
  }
  useEffect(t15, t16);
  const wrapperClasses = isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : "";
  const t17 = presetTheme || "";
  let t18;
  if ($[12] !== t17) {
    t18 = colorSettingDetection(t17);
    $[12] = t17;
    $[13] = t18;
  } else {
    t18 = $[13];
  }
  const colorSetting = t18;
  const mainClasses = `py-16 lg:py-[100px] ${presetTheme || ""} ${colorSetting}`;
  const headerClasses = `${layoutHeaderClasses} mb-4! `;
  const t19 = header || "";
  let t20;
  if ($[14] !== headerElement || $[15] !== t19) {
    t20 = renderHeaderConditionally(headerClasses, t19, headerElement);
    $[14] = headerElement;
    $[15] = t19;
    $[16] = t20;
  } else {
    t20 = $[16];
  }
  const finalHeader = t20;
  const t21 = presetTheme || "";
  let t22;
  if ($[17] !== t21) {
    t22 = darkTextureWrapper(t21);
    $[17] = t21;
    $[18] = t22;
  } else {
    t22 = $[18];
  }
  const componentStyles = t22;
  const hasFilters = filterGroups.length > 0;
  const hasHeader = eyebrow || header || subheader || description;
  const isFilterableVariant = displayType === "Filterable Card";
  let t23;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = mode => {
      setDrawerMode(mode);
      setDrawerOpen(true);
    };
    $[19] = t23;
  } else {
    t23 = $[19];
  }
  const openDrawer = t23;
  let t24;
  if ($[20] !== hasFilters || $[21] !== isMobile || $[22] !== labels?.labelForFilter || $[23] !== labels?.labelForSort || $[24] !== mobileStickyTop || $[25] !== sortOptions) {
    t24 = isMobile && (hasFilters || sortOptions.length > 0) ? <div className={MOBILE_FILTER_SORT_BAR} style={{
      top: mobileStickyTop
    }} data-testid="mobile-filter-sort-bar">{hasFilters && <button type="button" className="w-full py-3 bg-blue-trmb text-white text-sm font-bold uppercase tracking-wider rounded-lg border-none cursor-pointer flex items-center justify-center gap-2 hover:bg-blue-navy" onClick={() => openDrawer("filter")}><Icon type="t2u-collapse-close" size={14} />{labels?.labelForFilter || "Filter"}</button>}{sortOptions.length > 0 && <button type="button" className="w-full py-3 bg-white t2-dark:bg-gray-m9 text-gray-m9 t2-dark:text-white text-sm font-bold uppercase tracking-wider rounded-lg border border-gray-m3 t2-dark:border-gray-m7 cursor-pointer flex items-center justify-center gap-2 hover:bg-gray-m1 t2-dark:hover:bg-gray-m8" onClick={() => openDrawer("sort")}><Icon type="t2u-chevron-right" size={10} rotateDeg="270" /><Icon type="t2u-chevron-right" size={10} rotateDeg="90" />{labels?.labelForSort || "Sort"}</button>}</div> : null;
    $[20] = hasFilters;
    $[21] = isMobile;
    $[22] = labels?.labelForFilter;
    $[23] = labels?.labelForSort;
    $[24] = mobileStickyTop;
    $[25] = sortOptions;
    $[26] = t24;
  } else {
    t24 = $[26];
  }
  const mobileFilterSortButtons = t24;
  let t25;
  if ($[27] !== imageDisplayMode || $[28] !== isFilterableVariant || $[29] !== isLCP || $[30] !== labels?.labelForFeatured || $[31] !== labels?.labelForFeaturedCard) {
    t25 = (card, index) => <div key={`card-${index}`} className={CARD_WRAP} data-testid="filterable-card">{isFilterableVariant ? <FilterableCard {...card as FilterableCardProps} featuredLabel={labels?.labelForFeatured || "Featured"} featuredCardAriaLabel={labels?.labelForFeaturedCard || "Featured card"} /> : <MarketplaceCard {...card as MarketplaceCardProps} imageDisplayMode={(card as MarketplaceCardProps).imageDisplayMode ?? imageDisplayMode} isLCP={isLCP} />}</div>;
    $[27] = imageDisplayMode;
    $[28] = isFilterableVariant;
    $[29] = isLCP;
    $[30] = labels?.labelForFeatured;
    $[31] = labels?.labelForFeaturedCard;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  const renderCard = t25;
  let t26;
  if ($[33] !== buttons || $[34] !== currentSort || $[35] !== description || $[36] !== eyebrow || $[37] !== finalHeader || $[38] !== hasHeader || $[39] !== isMobile || $[40] !== labels?.labelForResult || $[41] !== labels?.labelForResults || $[42] !== labels?.labelForSortBy || $[43] !== onSearchChange || $[44] !== onSortChange || $[45] !== sortOptions || $[46] !== subheader || $[47] !== totalResults) {
    t26 = hasHeader && <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between pb-8 lg:pb-12 gap-4"><div className="flex-1">{eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}<div className="flex items-baseline gap-2 flex-wrap">{finalHeader}{totalResults > 0 && !onSearchChange && <span className="text-gray-m5 t2-dark:text-gray-m3 text-lg font-normal">({totalResults}{" "}{totalResults === 1 ? labels?.labelForResult || "Result" : labels?.labelForResults || "Results"})</span>}</div>{subheader && <div className={layoutSubheaderClasses}>{subheader}</div>}{description && <CommonDescription description={description} className="t2-layout__description mt-2" />}{buttons && buttons.length > 0 && <div className="flex gap-x-4 gap-y-3 flex-wrap mt-4">{buttons.map(_temp)}</div>}</div>{sortOptions.length > 0 && !isMobile && <div className="shrink-0 flex items-center gap-2"><label htmlFor="filterable-sort" className="text-sm text-gray-m7 t2-dark:text-gray-m3 whitespace-nowrap">{labels?.labelForSortBy || "Sort By"}:</label><div className="relative"><select id="filterable-sort" className={SORT_SELECT} value={currentSort} onChange={e => onSortChange?.(e.target.value)}>{sortOptions.map(_temp2)}</select><Icon type="t2u-chevron-right" size={10} rotateDeg="90" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-m5 t2-dark:text-gray-m3" /></div></div>}</div>;
    $[33] = buttons;
    $[34] = currentSort;
    $[35] = description;
    $[36] = eyebrow;
    $[37] = finalHeader;
    $[38] = hasHeader;
    $[39] = isMobile;
    $[40] = labels?.labelForResult;
    $[41] = labels?.labelForResults;
    $[42] = labels?.labelForSortBy;
    $[43] = onSearchChange;
    $[44] = onSortChange;
    $[45] = sortOptions;
    $[46] = subheader;
    $[47] = totalResults;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== activeFilters || $[50] !== filterGroups || $[51] !== hasFilters || $[52] !== isMobile || $[53] !== labels || $[54] !== onClearAllFilters || $[55] !== onFilterChange) {
    t27 = hasFilters && !isMobile && <FilterSidebar filterGroups={filterGroups} activeFilters={activeFilters} onFilterChange={(groupId, values) => onFilterChange?.(groupId, values)} onClearAll={() => onClearAllFilters?.()} labels={labels} />;
    $[49] = activeFilters;
    $[50] = filterGroups;
    $[51] = hasFilters;
    $[52] = isMobile;
    $[53] = labels;
    $[54] = onClearAllFilters;
    $[55] = onFilterChange;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== labels?.labelForResult || $[58] !== labels?.labelForResults || $[59] !== onSearchChange || $[60] !== resolvedSearchPlaceholder || $[61] !== searchTerm || $[62] !== totalResults) {
    t28 = (onSearchChange || totalResults > 0) && <div className="flex items-center justify-between gap-4 mb-6">{onSearchChange ? <div className="relative flex-1 max-w-[480px]"><Icon type="t2u-search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-m5 t2-dark:text-gray-m3" /><input type="text" role="searchbox" aria-label={resolvedSearchPlaceholder} placeholder={resolvedSearchPlaceholder} value={searchTerm ?? ""} onChange={e_0 => onSearchChange(e_0.target.value)} className="w-full pl-9 pr-3 py-2 text-sm bg-white t2-dark:bg-gray-m9 text-gray-m9 t2-dark:text-white placeholder-gray-m5 border border-gray-m3 t2-dark:border-gray-m7 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-trmb focus:border-blue-trmb" /></div> : <span aria-hidden={true} />}{totalResults > 0 && <span className="text-sm text-gray-m5 t2-dark:text-gray-m3 whitespace-nowrap">{totalResults}{" "}{totalResults === 1 ? labels?.labelForResult || "Result" : labels?.labelForResults || "Results"}</span>}</div>;
    $[57] = labels?.labelForResult;
    $[58] = labels?.labelForResults;
    $[59] = onSearchChange;
    $[60] = resolvedSearchPlaceholder;
    $[61] = searchTerm;
    $[62] = totalResults;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== cards || $[65] !== labels?.labelForNoFilterResults || $[66] !== renderCard) {
    t29 = cards.length === 0 ? <div className="py-16 text-center text-gray-m5 t2-dark:text-gray-m3 text-lg">{labels?.labelForNoFilterResults || "No results found. Try adjusting your filters."}</div> : <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-x-6 xl:gap-x-8 2xl:gap-x-10 gap-y-4">{cards.map((card_0, index_0) => renderCard(card_0, index_0))}</div>;
    $[64] = cards;
    $[65] = labels?.labelForNoFilterResults;
    $[66] = renderCard;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== currentPage || $[69] !== labels || $[70] !== onPageChange || $[71] !== onPageSizeChange || $[72] !== pageSize || $[73] !== pageSizeOptions || $[74] !== totalResults) {
    t30 = totalResults > 0 && onPageChange && onPageSizeChange && <Pagination currentPage={currentPage} pageSize={pageSize} totalResults={totalResults} pageSizeOptions={pageSizeOptions} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} labels={labels} />;
    $[68] = currentPage;
    $[69] = labels;
    $[70] = onPageChange;
    $[71] = onPageSizeChange;
    $[72] = pageSize;
    $[73] = pageSizeOptions;
    $[74] = totalResults;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== t28 || $[77] !== t29 || $[78] !== t30) {
    t31 = <div className="flex-1 min-w-0">{t28}{t29}{t30}</div>;
    $[76] = t28;
    $[77] = t29;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t27 || $[81] !== t31) {
    t32 = <div className="flex gap-5 lg:gap-10 flex-wrap">{t27}{t31}</div>;
    $[80] = t27;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  const t33 = currentSort || "";
  let t34;
  if ($[83] !== onFilterChange) {
    t34 = (groupId_0, values_0) => onFilterChange?.(groupId_0, values_0);
    $[83] = onFilterChange;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== onSortChange) {
    t35 = value => onSortChange?.(value);
    $[85] = onSortChange;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== onClearAllFilters) {
    t36 = () => onClearAllFilters?.();
    $[87] = onClearAllFilters;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => setDrawerOpen(false);
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  if ($[90] !== activeFilters || $[91] !== drawerMode || $[92] !== drawerOpen || $[93] !== filterGroups || $[94] !== labels || $[95] !== sortOptions || $[96] !== t33 || $[97] !== t34 || $[98] !== t35 || $[99] !== t36) {
    t38 = <MobileFilterDrawer isOpen={drawerOpen} mode={drawerMode} filterGroups={filterGroups} activeFilters={activeFilters} sortOptions={sortOptions} currentSort={t33} onFilterChange={t34} onSortChange={t35} onClearAll={t36} onClose={t37} labels={labels} />;
    $[90] = activeFilters;
    $[91] = drawerMode;
    $[92] = drawerOpen;
    $[93] = filterGroups;
    $[94] = labels;
    $[95] = sortOptions;
    $[96] = t33;
    $[97] = t34;
    $[98] = t35;
    $[99] = t36;
    $[100] = t38;
  } else {
    t38 = $[100];
  }
  let t39;
  if ($[101] !== mobileFilterSortButtons || $[102] !== t26 || $[103] !== t32 || $[104] !== t38 || $[105] !== wrapperClasses) {
    t39 = <div className={wrapperClasses}>{t26}{mobileFilterSortButtons}{t32}{t38}</div>;
    $[101] = mobileFilterSortButtons;
    $[102] = t26;
    $[103] = t32;
    $[104] = t38;
    $[105] = wrapperClasses;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  let t40;
  if ($[107] !== componentStyles || $[108] !== mainClasses || $[109] !== t39) {
    t40 = <div className={mainClasses} style={componentStyles} data-testid="layout-filterable-cards">{t39}</div>;
    $[107] = componentStyles;
    $[108] = mainClasses;
    $[109] = t39;
    $[110] = t40;
  } else {
    t40 = $[110];
  }
  return t40;
};
function _temp(btn, i) {
  return <TerraLink key={`btn-${btn.label}-${i}`} {...btn} />;
}
function _temp2(opt) {
  return <option key={opt.value} value={opt.value}>{opt.label}</option>;
}